/**
 * Right-hand visual for the landing hero. Stack of decorative SVGs that hints
 * at a rotating-QR ticket on a phone, plus a floating "valid" badge. Pure
 * SVG/CSS so it ships zero extra HTTP and scales perfectly on every screen.
 */
export function HeroVisual() {
  return (
    <div className="relative w-full max-w-[460px] mx-auto aspect-square">
      {/* Glow */}
      <div
        aria-hidden
        className="absolute inset-10 rounded-full blur-3xl opacity-25"
        style={{
          background: 'radial-gradient(circle, var(--color-accent), transparent 60%)',
        }}
      />
      {/* Subtle grid background */}
      <svg
        className="absolute inset-0 w-full h-full opacity-20"
        viewBox="0 0 400 400"
        fill="none"
        aria-hidden
      >
        <defs>
          <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#262626" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="400" height="400" fill="url(#grid)" />
      </svg>

      {/* Phone */}
      <svg viewBox="0 0 400 400" className="relative w-full h-full" aria-hidden>
        {/* Phone body */}
        <g transform="translate(110 30)">
          <rect
            x="0"
            y="0"
            width="180"
            height="340"
            rx="28"
            fill="#0a0a0a"
            stroke="#262626"
            strokeWidth="2"
          />
          <rect x="6" y="6" width="168" height="328" rx="22" fill="#111" />
          {/* Notch */}
          <rect x="70" y="14" width="40" height="6" rx="3" fill="#262626" />

          {/* Header inside phone */}
          <text
            x="20"
            y="50"
            fill="#fafafa"
            fontFamily="Inter, sans-serif"
            fontSize="9"
            fontWeight="700"
            letterSpacing="1.5"
          >
            BOLETRA
          </text>
          <text x="20" y="64" fill="#737373" fontFamily="Inter, sans-serif" fontSize="7">
            LIVE FEST · GA
          </text>

          {/* QR card */}
          <rect
            x="20"
            y="78"
            width="140"
            height="160"
            rx="14"
            fill="#0a0a0a"
            stroke="#404040"
            strokeWidth="1"
          />
          {/* QR pattern */}
          <g transform="translate(38 96)">
            <QrModules />
          </g>

          {/* Rotation halo */}
          <circle
            cx="90"
            cy="158"
            r="74"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="1.5"
            strokeDasharray="6 6"
            opacity="0.45"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 90 158"
              to="360 90 158"
              dur="18s"
              repeatCount="indefinite"
            />
          </circle>

          {/* Bottom info */}
          <text
            x="20"
            y="262"
            fill="#fafafa"
            fontFamily="Inter, sans-serif"
            fontSize="11"
            fontWeight="700"
          >
            Sofía Martínez
          </text>
          <text x="20" y="274" fill="#737373" fontFamily="Inter, sans-serif" fontSize="7">
            Dom · 22:00 · CDMX
          </text>

          {/* Pill: valid */}
          <rect x="20" y="288" width="140" height="32" rx="16" fill="var(--color-accent)" />
          <circle cx="36" cy="304" r="3" fill="#0a0a0a" />
          <text
            x="46"
            y="307"
            fill="#0a0a0a"
            fontFamily="Inter, sans-serif"
            fontSize="10"
            fontWeight="700"
          >
            Valid · Refreshes 0:23
          </text>
        </g>

        {/* Floating accent: scan beam */}
        <g opacity="0.85">
          <rect
            x="30"
            y="270"
            width="120"
            height="50"
            rx="14"
            fill="#0a0a0a"
            stroke="#262626"
            strokeWidth="1"
          />
          <circle cx="50" cy="295" r="9" fill="var(--color-accent)" />
          <path
            d="M46 295l3 3 6-6"
            stroke="#0a0a0a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <text
            x="68"
            y="290"
            fill="#fafafa"
            fontFamily="Inter, sans-serif"
            fontSize="8"
            fontWeight="700"
          >
            SCANNED
          </text>
          <text x="68" y="302" fill="#737373" fontFamily="Inter, sans-serif" fontSize="7">
            +1 entry · offline
          </text>
        </g>

        {/* Floating accent: revenue */}
        <g opacity="0.95">
          <rect
            x="240"
            y="50"
            width="130"
            height="56"
            rx="14"
            fill="#0a0a0a"
            stroke="#262626"
            strokeWidth="1"
          />
          <text
            x="252"
            y="68"
            fill="#737373"
            fontFamily="Inter, sans-serif"
            fontSize="7"
            letterSpacing="1.5"
          >
            TODAY
          </text>
          <text
            x="252"
            y="86"
            fill="#fafafa"
            fontFamily="Inter, sans-serif"
            fontSize="16"
            fontWeight="700"
          >
            $12,840
          </text>
          <text
            x="252"
            y="98"
            fill="var(--color-accent)"
            fontFamily="Inter, sans-serif"
            fontSize="7"
            fontWeight="600"
          >
            ▲ 24% · 286 tickets
          </text>
        </g>
      </svg>
    </div>
  );
}

function QrModules() {
  // Static-looking QR using deterministic placement of 8x8 blocks. It is
  // decorative only — no real data is encoded.
  const cells: Array<[number, number]> = [];
  const seed = 7;
  for (let r = 0; r < 14; r++) {
    for (let c = 0; c < 14; c++) {
      // Pseudo-random but stable across renders.
      const v = (r * 31 + c * 17 + seed * 13) % 7;
      if (v < 3) cells.push([r, c]);
    }
  }
  return (
    <g>
      {/* Three position markers */}
      {(
        [
          [0, 0],
          [0, 11],
          [11, 0],
        ] as const
      ).map(([r, c], i) => (
        <g key={i} transform={`translate(${c * 8} ${r * 8})`}>
          <rect width="22" height="22" fill="#fafafa" />
          <rect x="3" y="3" width="16" height="16" fill="#0a0a0a" />
          <rect x="6" y="6" width="10" height="10" fill="#fafafa" />
        </g>
      ))}
      {cells.map(([r, c], i) => {
        // Skip cells where the position markers live
        if (r < 3 && c < 3) return null;
        if (r < 3 && c > 10) return null;
        if (r > 10 && c < 3) return null;
        return <rect key={i} x={c * 8} y={r * 8} width="6" height="6" rx="1" fill="#fafafa" />;
      })}
    </g>
  );
}
